import { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const addYears = (date, years) => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

const formatMonthYear = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return MONTH_YEAR_FORMAT.format(date);
};

const toMonthStart = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/** e.g. "January 2026" or "January 2026 – June 2026" (month/year only, not datetime). */
const formatSelectedTimeline = (range) => {
  if (!range?.start || !range?.end) return '';
  const startLabel = formatMonthYear(range.start);
  const endLabel = formatMonthYear(range.end);
  if (!startLabel || !endLabel) return '';
  if (
    range.start.getFullYear() === range.end.getFullYear()
    && range.start.getMonth() === range.end.getMonth()
  ) {
    return startLabel;
  }
  return `${startLabel} – ${endLabel}`;
};

// Month-granularity range picker (span up to two years). Shows month/year
// next to the calendar icon; chosen { start, end } once both ends selected.
const MonthRangePicker = ({ range = null, setRange = () => {} }) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => toMonthStart(range?.start));
  const [endDate, setEndDate] = useState(() => toMonthStart(range?.end));
  const wrapperRef = useRef(null);
  const timelineLabel = useMemo(() => formatSelectedTimeline(range), [range]);

  useEffect(() => {
    setStartDate(toMonthStart(range?.start));
    setEndDate(toMonthStart(range?.end));
  }, [range]);

  const today = useMemo(() => new Date(), []);

  // Bounds are floored/ceiled to month boundaries. A month-picker click always
  // resolves to the 1st of the month, so if minDate/maxDate kept today's day
  // component the earliest/latest month would resolve outside the bound and
  // get rejected.
  const earliestSelectable = useMemo(
    () => new Date(today.getFullYear() - 2, today.getMonth(), 1),
    [today]
  );
  const latestSelectable = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999),
    [today]
  );

  // While only one end of the range is picked, clamp selectable months so the
  // span never exceeds two years (forwards or backwards via swapRange).
  const effectiveMinDate = useMemo(() => {
    if (startDate && !endDate) {
      const startMonth = toMonthStart(startDate);
      const twoYearsBeforeStart = addYears(startMonth, -2);
      return twoYearsBeforeStart > earliestSelectable
        ? twoYearsBeforeStart
        : earliestSelectable;
    }
    return earliestSelectable;
  }, [startDate, endDate, earliestSelectable]);

  const effectiveMaxDate = useMemo(() => {
    if (startDate && !endDate) {
      const startMonth = toMonthStart(startDate);
      const twoYearsAfterStart = addYears(startMonth, 2);
      return twoYearsAfterStart < latestSelectable ? twoYearsAfterStart : latestSelectable;
    }
    return latestSelectable;
  }, [startDate, endDate, latestSelectable]);

  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    const startMonth = toMonthStart(start);
    const endMonth = toMonthStart(end);
    setStartDate(startMonth);
    setEndDate(endMonth);
    if (startMonth && endMonth) {
      setRange({ start: startMonth, end: endMonth });
      setIsPickerOpen(false);
    }
  };

  const isRangeActive = Boolean(range?.start && range?.end);

  return (
    <div className='monthrange-picker' ref={wrapperRef}>
      <button
        type='button'
        aria-label={timelineLabel ? `Select month range, ${timelineLabel}` : 'Select month range'}
        aria-haspopup='dialog'
        aria-expanded={isPickerOpen}
        className={`monthrange-picker__toggle ${isRangeActive ? 'active' : ''}`}
        onClick={() => setIsPickerOpen((prev) => !prev)}
      >
        <FontAwesomeIcon icon={faCalendar} />
        {timelineLabel ? (
          <span className='monthrange-picker__label'>{timelineLabel}</span>
        ) : null}
      </button>
      {isPickerOpen && (
        <div className='monthrange-picker__popover'>
          <DatePicker
            selected={startDate}
            onChange={handleRangeChange}
            startDate={startDate}
            endDate={endDate}
            minDate={effectiveMinDate}
            maxDate={effectiveMaxDate}
            dateFormat='MM/yyyy'
            showMonthYearPicker
            selectsRange
            swapRange
            inline
          />
        </div>
      )}
    </div>
  );
};

export default MonthRangePicker;
